import { SelectInput, fontFamilies, colors, sharedButtonStyles, secondaryButtonTheme, Padding, Flexbox, IconTick, Stack, Text, IconWarning, Spacing, Modal, ModalBody, ModalHeader, ModalFooter, Button, MessageBox, SegmentedControl, Field, Input, IconLocationPin, IconBookmark } from '@newfront-insurance/core-ui';
import { useFormik } from 'formik';
import postalCodes from 'postal-codes-js';
import React, { useMemo, createContext, useContext, useState, useCallback, useEffect } from 'react';
import * as yup from 'yup';
import { useNotifications } from '@newfront-insurance/next-notifications';
import { u as useAdminTRPC } from './trpc-2b4d7b2f.esm.js';
import { core, internationalStreet } from 'smartystreets-javascript-sdk';
import styled, { css } from 'styled-components';
import Highlighter from 'react-highlight-words';

function transformSmartyStreetsLookupToAddress(suggestion) {
  const {
    streetLine,
    secondary,
    city,
    state,
    zipcode,
    entries
  } = suggestion;
  const address = {
    addressLine1: streetLine,
    addressLine2: secondary || '',
    addressLine3: '',
    city,
    countryCode: 'USA',
    formattedAddress: '',
    postalCode: zipcode,
    stateOrArea: state,
    entries
  };
  address.formattedAddress = formatStreetAddress(address);
  return address;
}
function transformFormValuesToAddress(formValues) {
  const {
    displayName,
    addressLine1,
    addressLine2,
    city,
    stateOrArea,
    postalCode,
    countryCode
  } = formValues;
  const address = {
    displayName,
    addressLine1,
    addressLine2,
    city,
    formattedAddress: '',
    countryCode: countryCode,
    stateOrArea,
    postalCode
  };
  address.formattedAddress = formatStreetAddress(address);
  return address;
}
function formatStreetAddressLines(address) {
  const {
    addressLine1,
    addressLine2,
    addressLine3
  } = address;
  let streetAddress = addressLine1;

  if (addressLine2) {
    streetAddress = `${streetAddress}, ${addressLine2}`;
  }

  if (addressLine3) {
    streetAddress = `${streetAddress}, ${addressLine3}`;
  }

  return streetAddress;
}
function formatCandidateToAddress(candidate) {
  const {
    administrativeArea,
    dependentLocality,
    locality,
    postalCode,
    countryIso3
  } = candidate.components;
  const newAddress = {
    addressLine1: candidate.address1,
    city: dependentLocality || locality,
    formattedAddress: '',
    countryCode: countryIso3,
    stateOrArea: administrativeArea,
    postalCode
  };
  newAddress.formattedAddress = formatStreetAddress(newAddress);
  return newAddress;
}
function formatStreetAddress(address) {
  const {
    city,
    stateOrArea,
    countryCode,
    postalCode
  } = address;
  const streetAddress = formatStreetAddressLines(address);
  return `${streetAddress}, ${city}, ${stateOrArea}, ${postalCode}, ${countryCode}`;
}

function useCreateAddress() {
  const {
    useMutation,
    invalidateQuery
  } = useAdminTRPC();
  const notifications = useNotifications();
  return useMutation('address.createAddress', {
    onSuccess: async (_, _ref) => {
      let {
        accountUuid
      } = _ref;
      notifications.add({
        type: 'success',
        title: 'Address has been created!'
      });
      await invalidateQuery(['address.getSavedAddress', {
        accountUuid
      }]);
    },
    onError: error => {
      notifications.addById('use-create-address-hook', {
        type: 'error',
        title: `There was a problem creating this address. ${error.message}`
      });
    }
  });
}

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * When the hook is called the SmartyStreets client must be available
 * on this context object.
 */
const SmartyStreetsInternationalContext = /*#__PURE__*/createContext(null);
/**
 * Provider props
 */

/**
 * Access the SmartyStreets client directly.
 * Throw an error if client is undefined
 */
function useSmartyStreetsInternationalClient() {
  const client = useContext(SmartyStreetsInternationalContext);

  if (!client) {
    throw new Error('SmartyStreets client must be set on SmartyStreetsInternationalContext');
  }

  return client;
}
/**
 * Create the SmartyStreets client
 */

function SmartyStreetsInternationalProvider(props) {
  const {
    apiKey,
    children
  } = props;
  const client = useMemo(() => {
    const credentials = new core.SharedCredentials(apiKey); // eslint-disable-next-line new-cap

    const clientBuilder = new core.ClientBuilder(credentials);
    return {
      internationalStreetClient: clientBuilder.buildInternationalStreetClient()
    };
  }, [apiKey]);
  return /*#__PURE__*/React.createElement(SmartyStreetsInternationalContext.Provider, {
    value: client
  }, children);
}

/**
 * This hook makes a usAutocomplete request when the search query changes and returns results.
 */
function useSmartyStreetsInternationalAddress() {
  const {
    internationalStreetClient
  } = useSmartyStreetsInternationalClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  const notifications = useNotifications();

  async function verifyInternationalAddress(countryCode, formattedAddress) {
    const lookup = new internationalStreet.Lookup(countryCode, formattedAddress);
    setLoading(true);
    setError(undefined);

    try {
      const res = await internationalStreetClient.send(lookup);
      return res;
    } catch (e) {
      const error = e;
      setError(error);
      notifications.addById('use-smartystreets-international-address', {
        title: `There was a problem verifying address.
        ${error.message}`,
        type: 'error'
      });
      throw e;
    } finally {
      setLoading(false);
    }
  }

  return [useCallback(verifyInternationalAddress, [internationalStreetClient, notifications]), loading, error];
}

async function getCountriesOptions() {
  const countriesQuery = await import('countries-code');
  const options = countriesQuery.allCountriesList().map(country => ({
    label: country.country_name_en,
    value: country.alpha3
  }));
  return options;
}

function CountryCodeSelect(_ref) {
  let {
    name,
    onChange,
    value,
    innerRef,
    disabled,
    placeholder = 'Select country',
    testId
  } = _ref;
  const [options, setOptions] = useState();
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    getCountriesOptions().then(countries => setOptions(countries));
  }, []);
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(SelectInput, {
    name: name,
    options: options || [],
    onChange: onChange,
    showSelectAnOption: !value,
    placeholder: placeholder,
    value: value,
    innerRef: innerRef,
    disabled: disabled,
    testId: testId
  }));
}

async function getCountryStateOptions(countryCode) {
  var _lookup$byIso;

  /**
   * pass iso3 country code to 'country-code-lookup' to get it's iso2
   * then pass the iso2, to 'country-state-city', to get the states
   */
  const lookup = (await import('country-code-lookup')).default;
  const countries = await import('country-state-city');
  const iso2 = (_lookup$byIso = lookup.byIso(countryCode)) === null || _lookup$byIso === void 0 ? void 0 : _lookup$byIso.iso2;
  if (!iso2) return [];
  const states = countries.State.getStatesOfCountry(iso2);
  const options = states.map(_ref => {
    let {
      name,
      isoCode
    } = _ref;
    return {
      label: name,
      value: isoCode
    };
  });
  return options;
}

function CountryStateSelect(_ref2) {
  let {
    countryCode = 'USA',
    name: elementName,
    onChange,
    value,
    innerRef,
    disabled,
    placeholder = 'Select state',
    testId
  } = _ref2;
  const [options, setOptions] = useState(); // eslint-disable-next-line @typescript-eslint/no-floating-promises

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    getCountryStateOptions(countryCode).then(countries => setOptions(countries));
  }, [countryCode]);
  return /*#__PURE__*/React.createElement(SelectInput, {
    name: elementName,
    options: options || [],
    onChange: onChange,
    showSelectAnOption: !value,
    placeholder: placeholder,
    value: value,
    innerRef: innerRef,
    disabled: disabled,
    testId: testId
  });
}

const sharedGridStyles = `
    display: grid;
    grid-template-columns: 100%;
    grid-row-gap: 16px;
  `;
const Styled2Column = styled.div`
  ${sharedGridStyles};
  @media screen and (min-width: ${_ref => {
  let {
    minWidth
  } = _ref;
  return minWidth || '800px';
}}) {
    grid-template-columns: 50% 50%;
    grid-column-gap: 0;
  }
`;
styled.div`
  ${sharedGridStyles};
  @media screen and (min-width: ${_ref2 => {
  let {
    minWidth
  } = _ref2;
  return minWidth || '800px';
}}) {
    grid-template-columns: 34% 32% 34%;
    grid-column-gap: 0;
  }
`;
const StyledAddressFieldWrapper = styled.div({
  position: 'relative'
});
const StyledAddressField = styled.div`
  appearance: none;
  background: white;
  border-radius: 3px;
  border: 1px solid #dbdbdb;
  box-sizing: border-box;
  color: #000;
  cursor: ${_ref3 => {
  let {
    cursor
  } = _ref3;
  return cursor || 'pointer';
}};
  display: block;
  font-family: ${fontFamilies.body};
  font-size: 14px;
  height: 40px;
  line-height: 21px;
  padding: 8px 28px 8px 12px;
  width: 100%;

  ${_ref4 => {
  let {
    disabled
  } = _ref4;
  return disabled && css`
      background: ${colors.steel[50]};
      cursor: not-allowed;
      pointer-events: none;
    `;
}};
`;
const StyledSelectedAddress = styled.span`
  white-space: nowrap;
  overflow-x: hidden;
  width: 100%;
  text-overflow: ellipsis;
  display: inline-block;
`;
const StyledIconWrapper$1 = styled.span`
    position: absolute;
    right: 16px;
    top 50%;
    transform: translateY(-50%);
  `;
const StyledResultsWrapper = styled.div`
  position: relative;
`;
const StyledDisabledResults = styled.div`
  background: rgba(255, 255, 255, 0.7);
  bottom: 0;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 5;
`;
const StyledScrollableResults = styled.div`
  max-height: 500px;
  overflow-y: auto;
`;
const StyledCreateAddressButton = styled.div`
  ${sharedButtonStyles(secondaryButtonTheme)}
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  // ensure StyledCreateAddressButton is clicked and not its contents
  :after {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    content: '';
  }
`;

function SelectableAddress(props) {
  const {
    isActive = false,
    onChange,
    candidate,
    enteredAddress
  } = props;
  let value = '';

  if (candidate) {
    const {
      formattedAddress
    } = formatCandidateToAddress(candidate);
    value = formattedAddress;
  }

  if (enteredAddress) {
    value = enteredAddress.formattedAddress;
  }

  const handleChange = e => {
    e.preventDefault();
    if (onChange) onChange(value);
  };

  const handleKeyDown = e => {
    if (e.key === ' ') {
      if (onChange) onChange(value);
    }
  };
  /**
   * getVerificationStatus
   * render a status message for Partial and Ambigious statuses only
   * (Unverified results won't make it to this component)
   * @param verificationStatus
   * @returns
   */


  function getVerificationStatus(verificationStatus) {
    if (verificationStatus === 'Verified') return;
    return /*#__PURE__*/React.createElement(Flexbox, {
      alignItems: "center"
    }, /*#__PURE__*/React.createElement(IconWarning, {
      color: verificationStatus !== 'None' ? colors.peach[500] : undefined,
      size: 12
    }), /*#__PURE__*/React.createElement(Spacing, {
      width: 4
    }), /*#__PURE__*/React.createElement(Text, {
      size: "small",
      color: colors.steel[400]
    }, "Only some parts of this address were verified"));
  }

  return /*#__PURE__*/React.createElement(StyledRow, {
    className: isActive ? 'active' : '',
    onClick: handleChange,
    onKeyDown: handleKeyDown,
    tabIndex: 0,
    role: "checkbox"
  }, /*#__PURE__*/React.createElement(Padding, {
    y: 12
  }, /*#__PURE__*/React.createElement(Flexbox, {
    alignItems: "center",
    justifyContent: "space-between"
  }, /*#__PURE__*/React.createElement(Flexbox, {
    alignItems: "flex-start"
  }, /*#__PURE__*/React.createElement(Padding, {
    right: 16
  }, /*#__PURE__*/React.createElement(StyledIconWrapper, null, isActive ? /*#__PURE__*/React.createElement(IconTick, null) : false)), /*#__PURE__*/React.createElement(Stack, {
    gap: 4,
    direction: "vertical"
  }, value && /*#__PURE__*/React.createElement(Text, {
    weight: 600
  }, value), candidate && getVerificationStatus(candidate.analysis.verificationStatus))))));
}
const StyledRow = styled.div`
  cursor: pointer;
`;
const StyledIconWrapper = styled.div`
  align-items: center;
  background-color: #fff;
  border: 1px solid ${colors.steel[200]};
  border-radius: 100%;
  display: flex;
  height: 20px;
  justify-content: center;
  width: 20px;

  .active & {
    background-color: ${colors.brand[400]};
    border-color: ${colors.brand[400]};
  }
`;

function VerifyInternationalAddressModal(_ref) {
  let {
    isOpen,
    handleClose,
    enteredAddress,
    candidates,
    onSelectAddress,
    width
  } = _ref;
  const [selectedCandidate, setSelectedCandidate] = useState();
  const [selectEnteredAddress, setSelectedAddress] = useState(false); // if there's no candidates, preselect the entered address

  if ((!candidates || candidates.length === 0) && !selectedCandidate && !selectEnteredAddress) {
    setSelectedAddress(true);
    setSelectedCandidate(enteredAddress);
  }

  function message(text) {
    return /*#__PURE__*/React.createElement(Padding, {
      y: 12
    }, /*#__PURE__*/React.createElement(MessageBox, {
      variant: "alert",
      message: text
    }));
  }

  return /*#__PURE__*/React.createElement(Modal, {
    isOpen: !!isOpen,
    onToggle: () => handleClose(),
    width: width
  }, /*#__PURE__*/React.createElement(ModalBody, null, /*#__PURE__*/React.createElement(ModalHeader, {
    titleText: "Verify international address",
    onClose: () => handleClose()
  }), /*#__PURE__*/React.createElement(Padding, {
    size: 24
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Text, null, "Verification results"), (!candidates || candidates.length === 0) && message('No verification results found for this address'), candidates.length > 0 && candidates.map(candidate => {
    const formattedCandidateAddress = formatCandidateToAddress(candidate);
    const status = candidate.analysis.verificationStatus;
    /**
     * If the candidate is unverified - dont display a SelectableOption.
     * Display a message and preselect the entered address
     */

    if (status === 'None') {
      if (!selectedCandidate && !selectEnteredAddress) {
        setSelectedAddress(true);
        setSelectedCandidate(enteredAddress);
      }

      return message(`This address could not be verified.
                    If you wish to proceed with your entered address please select it below,
                    otherwise click Cancel`);
    }

    return /*#__PURE__*/React.createElement(SelectableAddress, {
      key: formattedCandidateAddress.formattedAddress,
      isActive: selectEnteredAddress || !candidates || (selectedCandidate === null || selectedCandidate === void 0 ? void 0 : selectedCandidate.formattedAddress) === formattedCandidateAddress.formattedAddress,
      onChange: () => {
        const formattedCandidate = formatCandidateToAddress(candidate);
        setSelectedCandidate(formattedCandidate);
      },
      candidate: candidate
    });
  })), /*#__PURE__*/React.createElement(Text, null, "Entered address"), /*#__PURE__*/React.createElement(SelectableAddress, {
    isActive: selectEnteredAddress || (selectedCandidate === null || selectedCandidate === void 0 ? void 0 : selectedCandidate.formattedAddress) === enteredAddress.formattedAddress,
    onChange: () => setSelectedCandidate(enteredAddress),
    enteredAddress: enteredAddress
  })), /*#__PURE__*/React.createElement(ModalFooter, null, /*#__PURE__*/React.createElement(Flexbox, {
    alignItems: "center",
    justifyContent: "flex-end"
  }, /*#__PURE__*/React.createElement(Button, {
    type: "button",
    size: "secondary",
    onClick: () => handleClose()
  }, "Cancel"), /*#__PURE__*/React.createElement(Spacing, {
    width: 12
  }), /*#__PURE__*/React.createElement(Button, {
    type: "submit",
    disabled: !selectedCandidate,
    onClick: () => onSelectAddress(selectedCandidate)
  }, "Select address")))));
}

/* eslint-disable jsx-a11y/no-static-element-interactions */
const schema = yup.object().shape({
  addressLine1: yup.string().required('A street address is required'),
  city: yup.string().required('A city is required'),
  stateOrArea: yup.string().required('A state or area is required'),
  countryCode: yup.string().required('A country code is required'),
  postalCode: yup.string().test('postalCode', 'postalCode is not valid', (value, context) => {
    const validPostcode = postalCodes.validate(context.parent.countryCode, value ?? '');
    return typeof validPostcode === 'string' ? false : !!validPostcode;
  }).required('A zip/postal code is required')
});
function CreateAddressModal(_ref) {
  let {
    accountUuid,
    isOpen,
    toggle,
    onSubmit,
    width = '600px'
  } = _ref;
  const {
    mutateAsync: createAddress
  } = useCreateAddress();
  const [verifyInternationalAddress,, verifyInternationalAddressError] = useSmartyStreetsInternationalAddress();
  const [isValidating, setIsValidating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [internationalAddressResults, setInternationalAddressResults] = useState();
  const [selectedInternationalCandidate, setSelectedInternationalCandidate] = useState();
  const [locale, setLocale] = useState('US');
  const initialFormValues = {
    addressLine1: '',
    city: '',
    stateOrArea: '',
    postalCode: '',
    countryCode: 'USA',
    formattedAddress: ''
  };
  const form = useFormik({
    validateOnBlur: true,
    validationSchema: schema,
    initialValues: initialFormValues,
    onSubmit: async () => {
      setIsSubmitting(true);
      handleSubmit();
    }
  });
  /**
   * handleSelectedInternationalVerifiedAddress
   * If an address is selected from the verified international address modal,
   * and populate form fields with verified address
   * Then call handleSubmit
   */

  function handleSelectedInternationalVerifiedAddress(selectedAddress) {
    if (!selectedAddress) return;
    const {
      addressLine1,
      addressLine2,
      city,
      stateOrArea,
      postalCode
    } = selectedAddress;
    setSelectedInternationalCandidate(selectedAddress);
    form.setFieldValue('addressLine1', addressLine1);
    form.setFieldValue('addressLine2', addressLine2);
    form.setFieldValue('city', city);
    form.setFieldValue('stateOrArea', stateOrArea);
    form.setFieldValue('postalCode', postalCode);
    setIsSubmitting(true);
    handleSubmit();
  }
  /**
   * handleSubmit
   * Convert form values to an Address.
   * if so, return existingAddress, otherwise create new address and return it.
   */


  async function handleSubmit() {
    const address = transformFormValuesToAddress(form.values);
    const createdAddress = await createAddress({
      accountUuid,
      address: { ...address,
        formattedAddress: formatStreetAddress(address)
      }
    });
    setIsSubmitting(false);
    toggle(false);

    if (onSubmit && createdAddress) {
      onSubmit(createdAddress === null || createdAddress === void 0 ? void 0 : createdAddress.createdAddress);
    }
  }
  /**
   * If 'international' has been selected, we format form data into an address and
   * verify it with smartystreets and saving it's result.
   * Otherwise call handleSubmit()
   */


  const handleVerification = useCallback(() => {
    async function verifyCustomAddress() {
      setIsValidating(true);
      const {
        countryCode,
        ...address
      } = form.values;
      const formattedAddress = formatStreetAddress({ ...address,
        countryCode: countryCode
      });
      const response = await verifyInternationalAddress(countryCode, formattedAddress);
      setInternationalAddressResults(response === null || response === void 0 ? void 0 : response.result);
      setIsValidating(false);
    }

    if (locale === 'international') {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      verifyCustomAddress();
    } else {
      handleSubmit();
    }
  }, [form.isValid, locale]);
  /**
   * When the form is toggled,
   * ensure form is reset and state is cleared
   */

  useEffect(() => {
    if (!isOpen) {
      form.resetForm();
      setIsSubmitting(false);
      setIsValidating(false);
      setSelectedInternationalCandidate(undefined);
      setInternationalAddressResults(undefined);
      setLocale('US');
    }
  }, [isOpen]);
  return /*#__PURE__*/React.createElement(Modal, {
    isOpen: !!isOpen,
    onToggle: () => toggle(!isOpen),
    width: width
  }, /*#__PURE__*/React.createElement(ModalBody, null, /*#__PURE__*/React.createElement(ModalHeader, {
    titleText: "Add custom address",
    onClose: () => toggle(false)
  }), /*#__PURE__*/React.createElement(Padding, {
    size: 24
  }, /*#__PURE__*/React.createElement("form", {
    onSubmit: form.handleSubmit
  }, /*#__PURE__*/React.createElement(Stack, {
    direction: "vertical",
    gap: 16
  }, /*#__PURE__*/React.createElement(SegmentedControl, {
    value: locale,
    options: [{
      label: 'US',
      value: 'US'
    }, {
      label: 'International',
      value: 'international'
    }],
    onChange: val => setLocale(val)
  }), locale === 'international' && /*#__PURE__*/React.createElement(Field, {
    fieldId: "country-code",
    label: "Country"
  }, /*#__PURE__*/React.createElement(CountryCodeSelect, {
    value: form.values.countryCode,
    onChange: value => {
      // When the countryCode is selected, make sure
      // state is reset (and automatically repopulated in relevance to countryCode)
      // as well as stateOrArea/postalCode to ensure user data
      // is entered 'in order' and somewhat correctly.
      form.setFieldValue('countryCode', value);
      form.setFieldValue('city', '');
      form.setFieldValue('stateOrArea', '');
      form.setFieldValue('postalCode', '');
      setSelectedInternationalCandidate(undefined);
    }
  })), /*#__PURE__*/React.createElement(Field, {
    fieldId: "street-address-line-1",
    label: "Address line 1*",
    error: form.errors.addressLine1
  }, /*#__PURE__*/React.createElement(Input, {
    type: "text",
    name: "street-address-line-1",
    value: form.values.addressLine1,
    onChange: value => {
      form.setFieldValue('addressLine1', value);
    }
  })), /*#__PURE__*/React.createElement(Field, {
    fieldId: "street-address-line-2",
    label: "Address line 2"
  }, /*#__PURE__*/React.createElement(Input, {
    type: "text",
    name: "street-address-line-2",
    value: form.values.addressLine2,
    onChange: value => {
      form.setFieldValue('addressLine2', value);
    }
  })), /*#__PURE__*/React.createElement(Styled2Column, {
    minWidth: "600px"
  }, /*#__PURE__*/React.createElement(Padding, {
    right: 8
  }, /*#__PURE__*/React.createElement(Field, {
    fieldId: "city",
    label: "City*",
    error: form.errors.city
  }, /*#__PURE__*/React.createElement(Input, {
    type: "text",
    name: "city",
    value: form.values.city,
    onChange: value => {
      form.setFieldValue('city', value);
    }
  }))), /*#__PURE__*/React.createElement(Padding, {
    left: 8
  }, /*#__PURE__*/React.createElement(Field, {
    fieldId: "state",
    label: "State*",
    error: form.errors.stateOrArea
  }, /*#__PURE__*/React.createElement(CountryStateSelect, {
    value: form.values.stateOrArea,
    countryCode: form.values.countryCode,
    onChange: value => {
      form.setFieldValue('stateOrArea', value);
    }
  })))), /*#__PURE__*/React.createElement(Field, {
    fieldId: "zip",
    label: "Zip*",
    error: form.errors.postalCode
  }, /*#__PURE__*/React.createElement(Input, {
    type: "text",
    name: "zip",
    value: form.values.postalCode,
    onChange: value => {
      form.setFieldValue('postalCode', value);
    }
  })), !selectedInternationalCandidate && internationalAddressResults && internationalAddressResults.length > 0 && /*#__PURE__*/React.createElement(VerifyInternationalAddressModal, {
    candidates: internationalAddressResults,
    enteredAddress: transformFormValuesToAddress(form.values),
    isOpen: !!internationalAddressResults,
    onSelectAddress: handleSelectedInternationalVerifiedAddress,
    handleClose: () => {
      // We're not validating address info as its inputted in the form.
      // So, if the user cancels the verify modal (to perhaps tweak a field value), ...
      // we can clear some input values so the yup schema can place the form in an invalid state.
      // This forces the user to make required updates, and select city/state/zip again.
      // Otherwise the form just thinks it has complete string values (no validation of values) and wont
      // re-verify on an updated valid address.
      // Also it seems to makes sense to wipe these 3 related fields, rather than just 1 to ensure
      // data is entered 'in order' and 'correctly'
      form.setFieldValue('city', '');
      form.setFieldValue('stateOrArea', '');
      form.setFieldValue('postalCode', '');
      setInternationalAddressResults(undefined);
    },
    width: "500px"
  }), /*#__PURE__*/React.createElement(Flexbox, {
    alignItems: "center",
    justifyContent: "flex-end"
  }, /*#__PURE__*/React.createElement(Button, {
    type: "button",
    size: "secondary",
    onClick: () => toggle(false)
  }, "Cancel"), /*#__PURE__*/React.createElement(Spacing, {
    width: 12
  }), /*#__PURE__*/React.createElement(Button, {
    type: "button",
    onClick: handleVerification,
    loading: !verifyInternationalAddressError && (isSubmitting || isValidating),
    disabled: !!verifyInternationalAddressError || isSubmitting || isValidating || !form.isValid
  }, "Submit")))))));
}

function HighlightQueryInText(_ref) {
  let {
    text,
    query
  } = _ref;
  if (!query) return /*#__PURE__*/React.createElement("span", null, text);
  const equalisedText = text.toLowerCase();
  const equalisedQuery = query === null || query === void 0 ? void 0 : query.toLowerCase();
  return /*#__PURE__*/React.createElement(Highlighter, {
    highlightClassName: equalisedText.split(' ').join('-'),
    searchWords: equalisedQuery.split(' '),
    autoEscape: true,
    textToHighlight: text
  });
}

function LookupAddress(_ref) {
  let {
    onKeyDown,
    suggestion,
    handleSelectedResult,
    addressQuery
  } = _ref;
  const address = transformSmartyStreetsLookupToAddress(suggestion);
  const {
    addressLine1,
    addressLine2,
    addressLine3,
    city,
    stateOrArea,
    countryCode,
    postalCode
  } = address;
  return /*#__PURE__*/React.createElement(StyledResult$1, {
    tabIndex: 0,
    className: "lookup-result",
    onClick: () => handleSelectedResult(address),
    onKeyDown: e => {
      if (e.key === 'Enter') {
        handleSelectedResult(address);
        return;
      }

      onKeyDown(e);
    },
    "data-lookup-result": true
  }, /*#__PURE__*/React.createElement(Text, {
    size: "small",
    color: "inherit",
    style: {
      lineHeight: 1
    }
  }, /*#__PURE__*/React.createElement(Padding, {
    y: 8,
    x: 16
  }, /*#__PURE__*/React.createElement(Flexbox, null, /*#__PURE__*/React.createElement(Padding, {
    right: 12
  }, /*#__PURE__*/React.createElement(IconLocationPin, {
    size: 14,
    color: "#98A6B5"
  })), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Padding, {
    bottom: 4
  }, /*#__PURE__*/React.createElement(HighlightQueryInText, {
    text: addressLine1,
    query: addressQuery
  }), addressLine2 && /*#__PURE__*/React.createElement("div", null, addressLine2), addressLine3 && /*#__PURE__*/React.createElement("div", null, addressLine3)), /*#__PURE__*/React.createElement("div", null, city, ", ", stateOrArea, ", ", postalCode, ", ", countryCode))))));
}
const StyledResult$1 = styled.div`
  line-height: 1;

  &:hover,
  :focus {
    background-color: ${colors.brand[400]};
    cursor: pointer;
    color: #fff;
  }

  &:hover > span path,
  &:focus > span path {
    fill: #fff;
  }
`;

function SuggestedAddress(_ref) {
  let {
    onKeyDown,
    address,
    handleSelectedResult,
    addressQuery
  } = _ref;
  const {
    addressLine1,
    addressLine2,
    city,
    displayName,
    stateOrArea,
    countryCode,
    postalCode
  } = address;
  return /*#__PURE__*/React.createElement(StyledResult, {
    tabIndex: 0,
    onClick: () => handleSelectedResult(address),
    onKeyDown: e => {
      if (e.key === 'Enter') {
        handleSelectedResult(address);
        return;
      }

      onKeyDown(e);
    },
    "data-previously-saved-address": true
  }, /*#__PURE__*/React.createElement(Text, {
    size: "small",
    color: "inherit",
    style: {
      lineHeight: 1
    }
  }, /*#__PURE__*/React.createElement(Padding, {
    y: 8,
    x: 16
  }, /*#__PURE__*/React.createElement(Flexbox, null, /*#__PURE__*/React.createElement(Padding, {
    right: 12
  }, /*#__PURE__*/React.createElement(IconBookmark, {
    size: 14,
    color: "#98A6B5"
  })), /*#__PURE__*/React.createElement("div", null, displayName && /*#__PURE__*/React.createElement(Padding, {
    bottom: 4
  }, /*#__PURE__*/React.createElement(Text, {
    color: "inherit",
    size: "regular",
    style: {
      lineHeight: '1'
    }
  }, /*#__PURE__*/React.createElement(HighlightQueryInText, {
    text: displayName,
    query: addressQuery
  }))), /*#__PURE__*/React.createElement(Padding, {
    bottom: 4
  }, /*#__PURE__*/React.createElement(HighlightQueryInText, {
    text: addressLine1,
    query: addressQuery
  }), addressLine2 && /*#__PURE__*/React.createElement(HighlightQueryInText, {
    text: addressLine2,
    query: addressQuery
  })), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(HighlightQueryInText, {
    text: `${city}, ${stateOrArea}, ${postalCode}, ${countryCode}`,
    query: addressQuery
  })))))));
}
const StyledResult = styled.div`
  line-height: 1;

  &:hover,
  :focus {
    background-color: ${colors.brand[400]};
    cursor: pointer;
    color: #fff;
  }

  &:hover > span path,
  &:focus > span path {
    fill: #fff;
  }
`;

const SMARTY_STREETS_AUTOCOMPLETE_PUBLIC_KEY = // eslint-disable-next-line no-process-env
process.env.NODE_ENV === 'production' ? '29141061979865099' : '29141061880280959';

export { CreateAddressModal as C, LookupAddress as L, StyledAddressFieldWrapper as S, StyledAddressField as a, StyledSelectedAddress as b, StyledIconWrapper$1 as c, StyledScrollableResults as d, StyledResultsWrapper as e, formatStreetAddress as f, StyledDisabledResults as g, StyledCreateAddressButton as h, SuggestedAddress as i, SmartyStreetsInternationalProvider as j, SMARTY_STREETS_AUTOCOMPLETE_PUBLIC_KEY as k, useCreateAddress as u };
